import {
  CreateRecommendedFilterRequestDto,
  DiseaseGroupResponseDto,
  TherapistSearchRequestDtoOrderEnum,
  TherapistSearchRequestDtoOrderTermsEnum,
  TherapyMethodResponseDto,
} from '@app/api';
import { COLORS } from '@app/constants';
import { useClientSize } from '@app/hooks';
import { useHasMounted } from '@app/hooks/useHasMounted';
import { additionalFilters } from '@app/pageComponents/AllTherapistsPage/constants';

import {
  convertAppliedFiltersToQueryParams,
  convertDiseasesGroupsToFilterOptions,
  convertTherapyMethodsToFilterOptions,
  formatQueryParamsToFilter,
  mergeInitialFiltersAndFieldValues,
} from '@app/pageComponents/AllTherapistsPage/utils';

import { actions, selectors } from '@app/store';
import {
  useLazyGetDiseasesGroupsQuery,
  useLazyGetTherapyMethodsQuery,
} from '@app/store/api/common';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { IFilter } from '@app/types';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { NextComponentType, NextPageContext } from 'next';

import { BaseContext } from 'next/dist/shared/lib/utils';
import { isEmptyObject } from '@app/utils/isEmptyObject';
import { setKeyValueInObject } from '@app/utils/objectsTricks';
import { getInterviewFromStorage } from '@app/services/storageService';
import objectToGetParamsWithUrl from '@app/utils/objectToGetParamsWithUrl';
import { AllTherapistsTab } from '@app/pageComponents/AllTherapistsPage/AllTherapists';
import { checkIsMethodUrl } from '../pageComponents/AllTherapistsPage/AllTherapists/therapyMethods.redirects';

export type iLoadTherapistParamsFromPage = {
  searchValue?: string;
  activeTab?: any;
  sleepProblemsId?: any;
  hasInterviewDoneLocalStorage?: any;
};

interface iPageSettings {
	supervisorTherapists?: boolean;
	childTherapists?: boolean;
}

function withFilterLogic<
  C extends BaseContext = NextPageContext,
  IP = {},
  P = {},
>(Component: NextComponentType<C, IP, P>, settings?: iPageSettings ) {
  const MagicWithFilters = (props: P) => {
    const [isOpenInterviewModal, setIsOpenInterviewModal] = useState(true);

    const router = useRouter();
    const hasMounted = useHasMounted();
    const [page, setPage] = useState<number>(
      router?.query?.page ? +router.query.page : 1,
    );

    const { getIsBreakpoint } = useClientSize();
    const isMobile = getIsBreakpoint('sm');

    const dispatch = useAppDispatch();

    const isAuth = !!useAppSelector(selectors.auth.selectToken);

    const selectedFiltersDiseases = useAppSelector(
      selectors.allTherapistsFilters.selectSelectedFiltersDiseases,
    );

    const searchValue = useAppSelector(
      selectors.allTherapistsFilters.selectSearchValue,
    );

    const activeTab = useAppSelector(
      selectors.allTherapistsFilters.selectActiveTab,
    );

    const interview =
      hasMounted && getInterviewFromStorage()
        ? getInterviewFromStorage()
        : ({} as CreateRecommendedFilterRequestDto);

    const hasInterviewDoneLocalStorage = !isEmptyObject(interview);

    const [fetchDiseasesGroups, { isLoading: isLoadingDiseasesGroups }] =
      useLazyGetDiseasesGroupsQuery();

    const [fetchTherapyMethods, { isLoading: isLoadingTherapyMethods }] =
      useLazyGetTherapyMethodsQuery();

    const fetchQueryFilterParams = (
      filters: IFilter[],
      diseasesGroups: DiseaseGroupResponseDto[],
    ) => {
      if (isEmptyObject(router.query)) {
        return;
      }

      const { diseases, religions } = formatQueryParamsToFilter({
        query: router.query as { diseases: string | string[] },
        filters,
        diseasesGroups,
      });

      const selectedFilters = mergeInitialFiltersAndFieldValues({
        filters,
        data: { diseases, religions },
      });

      const queryFilters = convertAppliedFiltersToQueryParams({
        page,
        selectedFiltersValues,
        selectedFiltersDiseases,
        selectedSorting: sortParams,
      });

      dispatch(
        actions.allTherapistsFilters.setSelectedFilters({ selectedFilters }),
      );

      dispatch(actions.allTherapistsFilters.setQueryFilters({ queryFilters }));
    };

    const fetchFilterData = async () => {
      const [diseasesGroups, therapyMethods] = await Promise.all([
        fetchDiseasesGroups({ isChild: settings?.childTherapists }).unwrap(),
        fetchTherapyMethods().unwrap(),
      ]);

      dispatch(
        actions.allTherapistsFilters.setDiseasesGroups({ diseasesGroups }),
      );

      dispatch(
        actions.allTherapistsFilters.setTherapyMethods({ therapyMethods }),
      );

      return { diseasesGroups, therapyMethods };
    };

    const getTherapyMethodsFilter = (
      therapyMethods: TherapyMethodResponseDto[],
    ) => {
      return (
        therapyMethods?.length &&
        convertTherapyMethodsToFilterOptions(therapyMethods)
      );
    };

    const getDiseasesGroupsFilter = (
      diseasesGroups: DiseaseGroupResponseDto[],
    ) => {
      return (
        diseasesGroups.length &&
        convertDiseasesGroupsToFilterOptions(diseasesGroups)
      );
    };

    const getFilters = (
      therapyMethodsFilters: IFilter,
      diseasesGroupsFilters: IFilter[],
    ) => {
      const filters = [
        ...diseasesGroupsFilters,
        ...additionalFilters,
        therapyMethodsFilters,
      ];

      dispatch(actions.allTherapistsFilters.setFilters({ filters }));

      return filters;
    };

    const initiateFilterData = async () => {
      const { diseasesGroups, therapyMethods } = await fetchFilterData();

      const therapyMethodsFilters = getTherapyMethodsFilter(therapyMethods);
      const diseasesGroupsFilters = getDiseasesGroupsFilter(diseasesGroups);

      const filters =
        therapyMethodsFilters &&
        diseasesGroupsFilters &&
        getFilters(therapyMethodsFilters, diseasesGroupsFilters);

      filters && fetchQueryFilterParams(filters, diseasesGroups);

      await syncSelectedFiltersFromUrlParams(diseasesGroups, therapyMethods);
    };

    const selectedFiltersValues = useAppSelector(
      selectors.allTherapistsFilters.selectSelectedFiltersValues,
    );

    const sortParams = useAppSelector(
      selectors.allTherapistsFilters.selectSortParams,
    );

    const findDiseaseGroupIndexByDiseaseUrl = async (
      url: string,
      group: {
        id: string;
        name: string;
        items: Array<{
          id: string;
          name: string;
          url: string;
          description: string;
          diseasesGroupId: string;
        }>;
      }[],
    ): Promise<number | null> => {
      let index = null;

      for (let i = 0; i < group.length; i++) {
        const finded = group[i].items.find((el: any) => el?.url === url);
        if (finded) {
          index = i;
        }
      }

      return index;
    };

    const findMethodsGroupIndexByMethodUrl = async (
      url: string,
      group: {
        id: string;
        name: string;
        tip?: string | undefined;
        url: string;
      }[],
    ): Promise<number | null> => {
      let index = null;

      for (let i = 0; i < group.length; i++) {
        const finded = group[i].url === url;
        if (finded) {
          index = i;
        }
      }

      return index;
    };

    async function syncSelectedFiltersFromUrlParams(
      diseasesGroups: DiseaseGroupResponseDto[],
      therapyMethods: TherapyMethodResponseDto[],
    ) {
      const p = router.query;

      let updSelectedFiltersValues: any = {};

      if (p?.diseases?.length) {
        // sync diseases
        for (const diseaseInQueryUrl of p.diseases) {
          const index = await findDiseaseGroupIndexByDiseaseUrl(
            diseaseInQueryUrl,
            diseasesGroups,
          );
          if ((!index && index !== 0) || !diseaseInQueryUrl) {
            continue;
          }

          const filterKeyName = `diseases.${index.toString()}`;

          const filterKey = updSelectedFiltersValues?.[filterKeyName];

          if (filterKey && filterKey.includes(diseaseInQueryUrl)) {
            continue;
          }

          updSelectedFiltersValues = {
            ...updSelectedFiltersValues,
            [filterKeyName]: filterKey
              ? [...filterKey, diseaseInQueryUrl]
              : [diseaseInQueryUrl],
          };
        }
      }

      // if only one disease selected
      const urlParam = router.query?.diseaseUrl;
      const isMethodUrl = urlParam && typeof urlParam === 'string' && checkIsMethodUrl(urlParam);
      const diseaseUrl = isMethodUrl || typeof urlParam !== 'string' ? undefined : urlParam;
      const therapyMethodUrl = isMethodUrl ? urlParam : undefined;

      if (diseaseUrl) {
        const index = await findDiseaseGroupIndexByDiseaseUrl(
          diseaseUrl,
          diseasesGroups,
        );

        if ((index || index === 0) && diseaseUrl) {
          const filterKeyName = `diseases.${index.toString()}`;

          const updatedMethod = updSelectedFiltersValues?.[filterKeyName]
            ?.length
            ? [
                ...updSelectedFiltersValues[filterKeyName],
                diseaseUrl,
              ]
            : [diseaseUrl];

          updSelectedFiltersValues = {
            ...updSelectedFiltersValues,
            [filterKeyName]: updatedMethod,
          };
        }
      }
      
      if (therapyMethodUrl) {
        const index = await findMethodsGroupIndexByMethodUrl(
          therapyMethodUrl,
          therapyMethods,
        );
        
        if ((index || index === 0) && therapyMethodUrl) {
          const filterKeyName = 'therapyMethods';
          const updatedMethods = updSelectedFiltersValues?.[filterKeyName]
            ?.length
            ? [
                ...updSelectedFiltersValues[filterKeyName],
                therapyMethods[index].id,
              ]
            : [therapyMethods[index].id];

          updSelectedFiltersValues = {
            ...updSelectedFiltersValues,
            [filterKeyName]: updatedMethods,
          };
        }
      }

      // sync other

      if (p?.therapyMethods)
        updSelectedFiltersValues = setKeyValueInObject(
          'therapyMethods',
          Array.isArray(p.therapyMethods)
            ? p.therapyMethods
            : [p.therapyMethods],
          updSelectedFiltersValues,
        );

      if (p?.workWithLgbt)
        updSelectedFiltersValues = setKeyValueInObject(
          'workWithLgbt',
          [true],
          updSelectedFiltersValues,
        );

      if (p?.workWithEthnicGroups)
        updSelectedFiltersValues = setKeyValueInObject(
          'workWithEthnicGroups',
          [true],
          updSelectedFiltersValues,
        );

      if (p?.gender)
        updSelectedFiltersValues = setKeyValueInObject(
          'gender',
          [p.gender],
          updSelectedFiltersValues,
        );

      if (p?.dayLimit)
        updSelectedFiltersValues = setKeyValueInObject(
          'dayLimit',
          p.dayLimit,
          updSelectedFiltersValues,
        );

      if (p?.priceMax)
        updSelectedFiltersValues = setKeyValueInObject(
          'priceMax',
          [+p.priceMax],
          updSelectedFiltersValues,
        );

      if (p?.isApproved)
        updSelectedFiltersValues = setKeyValueInObject(
          'isApproved',
          p.isApproved,
          updSelectedFiltersValues,
        );

      if (p?.religions)
        updSelectedFiltersValues = setKeyValueInObject(
          'religions',
          Array.isArray(p.religions) ? p.religions : [p.religions],
          updSelectedFiltersValues,
        );

      if (p?.search && !Array.isArray(p?.search)) {
        dispatch(actions.allTherapistsFilters.setSearchValue(p.search));
      }

      dispatch(
        actions.allTherapistsFilters.setSelectedFiltersValues({
          selectedFiltersValues: updSelectedFiltersValues,
        }),
      );
    }

    function applyAndSaveAllSelectedFilters(p: number | undefined, childAgeValue: number | undefined, therapistAgeValue: string | undefined) {
      const queryFilters = convertAppliedFiltersToQueryParams({
        page: p || page,
        selectedFiltersValues,
        selectedFiltersDiseases,
        selectedSorting: sortParams,
        tab: activeTab || AllTherapistsTab.all,
        searchValue,
        childOld: childAgeValue,
        therapistOld: therapistAgeValue,
      });

      let pathname = settings?.supervisorTherapists
				? '/supervisors'
				: settings?.childTherapists
					? '/child-therapists'
					: '/all-therapists';

      if (queryFilters?.diseases?.length) {
        if (typeof queryFilters.diseases === 'string') {
          pathname += `/${queryFilters.diseases}`;
        } else if (queryFilters.diseases?.length === 1) {
          pathname += `/${queryFilters.diseases[0]}`;
          queryFilters.diseases = queryFilters.diseases.filter(
            (el) => el !== queryFilters?.diseases?.[0],
          );
        }
      }

      const currentDomain = window.location.origin;
      const urlWithParams = `${currentDomain}${pathname}?${objectToGetParamsWithUrl(
        queryFilters,
      )}`;
      window.location.href = urlWithParams;
    }

    useEffect(() => {
      router.isReady && initiateFilterData();
      router.query?.page && setPage(+router.query.page);
      router.query?.order &&
        router.query?.orderTerms &&
        dispatch(
          actions.allTherapistsFilters.setSortingParams({
            sortingPayload: {
              order: router.query?.order as TherapistSearchRequestDtoOrderEnum,
              orderTerms: router.query
                ?.orderTerms as TherapistSearchRequestDtoOrderTermsEnum,
            },
          }),
        );
    }, [router?.isReady]);

    // const ComponentTyped = Component as React.ComponentType<P>;

    const isLoadingPage =
      !router.isReady || isLoadingDiseasesGroups || isLoadingTherapyMethods;

    const shouldShowModal = false; // Decided hide modal for client interview (11.17.23)
    // const shouldShowModal =
    //   !hasShownInterviewModal &&
    //   user?.role === UserResponseDtoRoleEnum.Patient &&
    //   !metadata?.allowedRecommendedSearch &&
    //   user?.firstname;

    return (
      <Component
        {...props}
        page={page}
        setPage={setPage}
        isLoadingPage={isLoadingPage}
        isMobile={isMobile}
        hasMounted={hasMounted}
        shouldShowModal={shouldShowModal}
        isOpenInterviewModal={isOpenInterviewModal}
        setIsOpenInterviewModal={setIsOpenInterviewModal}
        applyAndSaveAllSelectedFilters={applyAndSaveAllSelectedFilters}
      />
    );
  };

  return MagicWithFilters;
}

const Root = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.white};
`;

export default withFilterLogic;
